import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

const SLIDES = [
  {
    id: 'hero-1',
    image:
      'https://readdy.ai/api/search-image?query=Cinematic%20wide%20shot%20of%20a%20young%20couple%20standing%20apart%20on%20a%20rain%20slicked%20city%20street%20at%20dusk%2C%20moody%20deep%20blue%20tones%20with%20warm%20orange%20street%20light%20accents%2C%20dramatic%20bokeh%2C%20emotional%20tension%2C%20film%20still%20aesthetic%2C%20dark%20atmospheric%20background%20with%20negative%20space%20at%20top%20for%20text%20overlay%2C%20high%20detail%2C%2035mm%20look&width=1600&height=900&seq=fsa2-hero-1&orientation=landscape',
    tag: '官方发布',
    title: '《疯狂试爱2》正式官宣定档，全新预告片同步曝光',
    desc: '两分钟预告以一段未完成的对话开场，迅速点燃影迷讨论热情。',
    to: '/detail/news-001',
  },
  {
    id: 'hero-2',
    image:
      'https://readdy.ai/api/search-image?query=Dramatic%20cinematic%20film%20still%20of%20a%20thoughtful%20figure%20looking%20out%20a%20window%20in%20a%20dim%20apartment%2C%20deep%20blue%20night%20ambience%20with%20a%20single%20warm%20orange%20lamp%20glow%2C%20soft%20cinematic%20shadows%2C%20romantic%20drama%20mood%2C%20dark%20atmospheric%20background%20with%20negative%20space%2C%20shallow%20depth%20of%20field%2C%20high%20detail&width=1600&height=900&seq=fsa2-hero-2&orientation=landscape',
    tag: '深度剧情',
    title: '《疯狂试爱2》结局深度解析：那些被忽略的温柔伏笔',
    desc: '结尾留下的并非答案，而是一个更温柔的问题。',
    to: '/detail/plot-003',
  },
  {
    id: 'hero-3',
    image:
      'https://readdy.ai/api/search-image?query=Cinematic%20film%20still%20of%20a%20lone%20figure%20walking%20along%20an%20empty%20illuminated%20bridge%20at%20night%2C%20deep%20navy%20blue%20atmosphere%20contrasted%20by%20warm%20orange%20city%20lights%2C%20reflective%20wet%20pavement%2C%20melancholic%20romantic%20mood%2C%20dark%20moody%20background%20with%20negative%20space%2C%2035mm%20cinematic%20look%2C%20high%20detail&width=1600&height=900&seq=fsa2-hero-3&orientation=landscape',
    tag: '幕后花絮',
    title: '《疯狂试爱2》幕后花絮：一场戏反复打磨三十次的执着',
    desc: '四十秒的高光片段，背后是不为人知的耐心。',
    to: '/detail/feature-001',
  },
];

export default function HeroCarousel() {
  const [current, setCurrent] = useState(0);
  const timerRef = useRef<number | null>(null);
  const touchStart = useRef(0);

  const goTo = useCallback((index: number) => {
    setCurrent((index + SLIDES.length) % SLIDES.length);
  }, []);

  const next = useCallback(() => goTo(current + 1), [current, goTo]);
  const prev = useCallback(() => goTo(current - 1), [current, goTo]);

  useEffect(() => {
    timerRef.current = window.setInterval(() => {
      setCurrent((c) => (c + 1) % SLIDES.length);
    }, 5000);
    return () => {
      if (timerRef.current) window.clearInterval(timerRef.current);
    };
  }, []);

  const pause = () => {
    if (timerRef.current) window.clearInterval(timerRef.current);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStart.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    const delta = e.changedTouches[0].clientX - touchStart.current;
    if (Math.abs(delta) > 50) {
      if (delta < 0) next();
      else prev();
    }
  };

  return (
    <section
      className="relative h-[440px] w-full overflow-hidden md:h-[600px]"
      onMouseEnter={pause}
      onMouseLeave={() => {
        timerRef.current = window.setInterval(() => {
          setCurrent((c) => (c + 1) % SLIDES.length);
        }, 5000);
      }}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      aria-label="焦点图轮播"
    >
      {SLIDES.map((slide, index) => (
        <div
          key={slide.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            index === current ? 'opacity-100' : 'pointer-events-none opacity-0'
          }`}
        >
          <img src={slide.image} alt={slide.title} className="h-full w-full object-cover object-top" />
          <div className="absolute inset-0 bg-gradient-to-r from-primary-950/90 via-primary-950/70 to-primary-950/40"></div>
        </div>
      ))}

      <div className="relative z-10 mx-auto flex h-full max-w-[1280px] flex-col justify-center px-4 md:px-8">
        <div className="max-w-2xl">
          <span className="inline-flex items-center gap-2 rounded-full bg-accent-500/90 px-3 py-1 text-xs font-semibold text-background-50">
            <i className="ri-movie-line"></i>
            {SLIDES[current].tag}
          </span>
          <h1 className="mt-4 font-heading text-2xl font-bold leading-tight text-background-50 md:text-4xl lg:text-5xl">
            {SLIDES[current].title}
          </h1>
          <p className="mt-4 max-w-xl text-sm leading-relaxed text-primary-100 md:text-base">
            {SLIDES[current].desc}
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <Link
              to={SLIDES[current].to}
              className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-5 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-accent-600"
            >
              <i className="ri-play-circle-line text-lg"></i>
              查看详情
            </Link>
            <Link
              to="/news"
              className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-primary-200/50 px-5 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-800/60"
            >
              浏览全部资讯
            </Link>
          </div>
        </div>
      </div>

      <div className="absolute bottom-6 left-1/2 z-10 flex -translate-x-1/2 items-center gap-3">
        <button
          type="button"
          aria-label="上一张"
          onClick={prev}
          className="flex h-9 w-9 items-center justify-center rounded-full border border-background-50/40 text-background-50 transition-colors hover:bg-background-50 hover:text-primary-900 cursor-pointer md:h-10 md:w-10"
        >
          <i className="ri-arrow-left-s-line text-xl"></i>
        </button>
        <div className="flex items-center gap-2">
          {SLIDES.map((slide, index) => (
            <button
              key={slide.id}
              type="button"
              aria-label={`切换到第 ${index + 1} 张`}
              onClick={() => goTo(index)}
              className={`h-2 rounded-full transition-all cursor-pointer ${
                index === current ? 'w-6 bg-accent-500' : 'w-2 bg-background-50/60 hover:bg-background-50'
              }`}
            ></button>
          ))}
        </div>
        <button
          type="button"
          aria-label="下一张"
          onClick={next}
          className="flex h-9 w-9 items-center justify-center rounded-full border border-background-50/40 text-background-50 transition-colors hover:bg-background-50 hover:text-primary-900 cursor-pointer md:h-10 md:w-10"
        >
          <i className="ri-arrow-right-s-line text-xl"></i>
        </button>
      </div>
    </section>
  );
}